import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, Button, Input, Badge, Modal, Table, Pagination } from '@/components/ui';
import { UserRoleBadge } from '@/components/admin';
import type { Column } from '@/components/ui';
import { adminService } from '@/services/admin.service';
import type { AdminUser, AuditLog } from '@/services/admin.service';
import type { UserRole, UpdateUserRequest } from '@/types';

const ROLES: { value: UserRole; label: string }[] = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'BROKER', label: 'Broker' },
  { value: 'AGENT', label: 'Agent' },
  { value: 'STAFF', label: 'Staff' },
];

const getActionBadgeVariant = (action: AuditLog['action']) => {
  switch (action) {
    case 'CREATE':
      return 'success';
    case 'UPDATE':
      return 'info';
    case 'DELETE':
      return 'danger';
    case 'LOGIN':
    case 'LOGOUT':
      return 'default';
    case 'APPROVE':
    case 'ACTIVATE':
      return 'success';
    case 'REJECT':
    case 'DEACTIVATE':
      return 'warning';
    default:
      return 'default';
  }
};

export const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Activity log state
  const [activities, setActivities] = useState<AuditLog[]>([]);
  const [isLoadingActivity, setIsLoadingActivity] = useState(false);
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotalPages, setActivityTotalPages] = useState(1);
  const [activityTotalItems, setActivityTotalItems] = useState(0);

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<UpdateUserRequest>({});
  const [isSaving, setIsSaving] = useState(false);

  // Modal states
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [isRoleChangeModalOpen, setIsRoleChangeModalOpen] = useState(false);
  const [pendingRole, setPendingRole] = useState<UserRole | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchUser = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      const data = await adminService.getUserById(id);
      setUser(data);
      setEditForm({
        firstName: data.firstName,
        lastName: data.lastName,
        role: data.role,
        phone: data.phone || '',
      });
    } catch {
      setError('Failed to load user details');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const fetchActivity = useCallback(async () => {
    if (!id) return;

    setIsLoadingActivity(true);
    try {
      const response = await adminService.getUserActivity(id, activityPage, 10);
      setActivities(response.data);
      setActivityTotalPages(response.meta.totalPages);
      setActivityTotalItems(response.meta.total);
    } catch {
      console.error('Failed to load user activity');
    } finally {
      setIsLoadingActivity(false);
    }
  }, [id, activityPage]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  const handleSave = async () => {
    if (!id) return;

    setIsSaving(true);
    try {
      const updated = await adminService.updateUser(id, editForm);
      setUser(updated);
      setIsEditing(false);
      setSuccessMessage('User updated successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch {
      setError('Failed to update user');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!id || !user) return;

    setIsProcessing(true);
    try {
      const updated = user.isActive
        ? await adminService.deactivateUser(id)
        : await adminService.activateUser(id);
      setUser(updated);
      setIsDeactivateModalOpen(false);
      setSuccessMessage(`User ${user.isActive ? 'deactivated' : 'activated'} successfully`);
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchActivity(); // Refresh activity log
    } catch {
      setError('Failed to update user status');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResetPassword = async () => {
    if (!id) return;

    setIsProcessing(true);
    try {
      await adminService.resetPassword(id);
      setIsResetPasswordModalOpen(false);
      setSuccessMessage('Password reset email sent successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch {
      setError('Failed to send password reset email');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRoleChange = (newRole: UserRole) => {
    if (newRole !== user?.role) {
      setPendingRole(newRole);
      setIsRoleChangeModalOpen(true);
    }
  };

  const confirmRoleChange = async () => {
    if (!id || !pendingRole) return;

    setIsProcessing(true);
    try {
      const updated = await adminService.updateUser(id, { ...editForm, role: pendingRole });
      setUser(updated);
      setEditForm({ ...editForm, role: pendingRole });
      setIsRoleChangeModalOpen(false);
      setPendingRole(null);
      setSuccessMessage('User role updated successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchActivity();
    } catch {
      setError('Failed to update user role');
    } finally {
      setIsProcessing(false);
    }
  };

  const activityColumns: Column<AuditLog>[] = [
    {
      key: 'createdAt',
      header: 'Date',
      render: (log) => (
        <span className="text-sm text-secondary-500">
          {new Date(log.createdAt).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      render: (log) => (
        <Badge variant={getActionBadgeVariant(log.action)}>{log.action}</Badge>
      ),
    },
    {
      key: 'tableName',
      header: 'Resource',
      render: (log) => (
        <span className="text-sm text-secondary-700">{log.tableName}</span>
      ),
    },
    {
      key: 'details',
      header: 'Details',
      render: (log) => (
        <div className="max-w-xs">
          {log.newValues && (
            <p className="text-xs text-secondary-500 truncate">
              {JSON.stringify(log.newValues).slice(0, 50)}...
            </p>
          )}
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-secondary-200 rounded mb-6"></div>
          <div className="h-64 bg-secondary-200 rounded-xl"></div>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="space-y-6">
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
          <p className="text-danger-700">{error || 'User not found'}</p>
        </div>
        <Button variant="secondary" onClick={() => navigate('/users')}>
          Back to Users
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="bg-success-50 border border-success-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <p className="text-success-700">{successMessage}</p>
            <button
              onClick={() => setSuccessMessage(null)}
              className="text-success-500 hover:text-success-700"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-4">
          <Link
            to="/users"
            className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
          >
            <svg
              className="w-5 h-5 text-secondary-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </Link>
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
              {user.avatar ? (
                <img
                  src={user.avatar}
                  alt={`${user.firstName} ${user.lastName}`}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <span className="text-2xl font-medium text-primary-700">
                  {user.firstName.charAt(0)}
                  {user.lastName.charAt(0)}
                </span>
              )}
            </div>
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-2xl font-bold text-secondary-900">
                  {user.firstName} {user.lastName}
                </h1>
                <Badge variant={user.isActive ? 'success' : 'danger'} dot>
                  {user.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <p className="mt-1 text-sm text-secondary-500">{user.email}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {!isEditing ? (
            <>
              <Button variant="secondary" onClick={() => setIsEditing(true)}>
                Edit User
              </Button>
              <Button
                variant={user.isActive ? 'danger' : 'primary'}
                onClick={() => setIsDeactivateModalOpen(true)}
              >
                {user.isActive ? 'Deactivate' : 'Activate'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} isLoading={isSaving}>
                Save Changes
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <Card.Header>
              <h2 className="text-lg font-semibold text-secondary-900">
                User Information
              </h2>
            </Card.Header>
            <Card.Body>
              {isEditing ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="First Name"
                    value={editForm.firstName || ''}
                    onChange={(e) =>
                      setEditForm({ ...editForm, firstName: e.target.value })
                    }
                  />
                  <Input
                    label="Last Name"
                    value={editForm.lastName || ''}
                    onChange={(e) =>
                      setEditForm({ ...editForm, lastName: e.target.value })
                    }
                  />
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                      Role
                    </label>
                    <select
                      value={editForm.role || ''}
                      onChange={(e) => handleRoleChange(e.target.value as UserRole)}
                      className="w-full px-4 py-2.5 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {ROLES.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Input
                    label="Phone"
                    value={editForm.phone || ''}
                    onChange={(e) =>
                      setEditForm({ ...editForm, phone: e.target.value })
                    }
                  />
                </div>
              ) : (
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-secondary-500">
                      Full Name
                    </dt>
                    <dd className="mt-1 text-sm text-secondary-900">
                      {user.firstName} {user.lastName}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-secondary-500">
                      Email
                    </dt>
                    <dd className="mt-1 text-sm text-secondary-900">
                      {user.email}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-secondary-500">
                      Role
                    </dt>
                    <dd className="mt-1">
                      <UserRoleBadge role={user.role} />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-secondary-500">
                      Phone
                    </dt>
                    <dd className="mt-1 text-sm text-secondary-900">
                      {user.phone || 'Not provided'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-secondary-500">
                      Last Login
                    </dt>
                    <dd className="mt-1 text-sm text-secondary-900">
                      {user.lastLoginAt
                        ? new Date(user.lastLoginAt).toLocaleString()
                        : 'Never'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-secondary-500">
                      Joined
                    </dt>
                    <dd className="mt-1 text-sm text-secondary-900">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </dd>
                  </div>
                </dl>
              )}
            </Card.Body>
          </Card>

          {/* Activity Log */}
          <Card padding="none">
            <div className="px-6 py-4 border-b border-secondary-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-secondary-900">
                  Activity Log
                </h2>
                <span className="text-sm text-secondary-500">
                  {activityTotalItems} entries
                </span>
              </div>
            </div>
            <Table
              columns={activityColumns}
              data={activities}
              keyExtractor={(log) => log.id}
              isLoading={isLoadingActivity}
              emptyMessage="No activity recorded"
            />
            {activityTotalPages > 1 && (
              <div className="px-6 py-4 border-t border-secondary-200">
                <Pagination
                  currentPage={activityPage}
                  totalPages={activityTotalPages}
                  totalItems={activityTotalItems}
                  itemsPerPage={10}
                  onPageChange={setActivityPage}
                />
              </div>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Wallet Balance (for brokers) */}
          {(user.role === 'BROKER' || user.walletBalance !== undefined) && (
            <Card>
              <Card.Header>
                <h2 className="text-lg font-semibold text-secondary-900">
                  Wallet Balance
                </h2>
              </Card.Header>
              <Card.Body>
                <p className="text-3xl font-bold text-secondary-900">
                  ${(user.walletBalance ?? 0).toFixed(2)}
                </p>
                <p className="mt-1 text-sm text-secondary-500">
                  Available balance
                </p>
              </Card.Body>
            </Card>
          )}

          {/* Tenant Info */}
          <Card>
            <Card.Header>
              <h2 className="text-lg font-semibold text-secondary-900">Tenant</h2>
            </Card.Header>
            <Card.Body>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-secondary-900">
                    {user.tenantName}
                  </p>
                  <p className="text-xs text-secondary-500">ID: {user.tenantId}</p>
                </div>
                <Link
                  to={`/tenants/${user.tenantId}`}
                  className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
                >
                  View Tenant Details
                  <svg
                    className="ml-1 w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Link>
              </div>
            </Card.Body>
          </Card>

          {/* Actions */}
          <Card>
            <Card.Header>
              <h2 className="text-lg font-semibold text-secondary-900">Actions</h2>
            </Card.Header>
            <Card.Body className="space-y-3">
              <Button
                variant="secondary"
                fullWidth
                onClick={() => setIsResetPasswordModalOpen(true)}
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                  />
                </svg>
                Send Password Reset
              </Button>
              <Button
                variant={user.isActive ? 'danger' : 'primary'}
                fullWidth
                onClick={() => setIsDeactivateModalOpen(true)}
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {user.isActive ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  )}
                </svg>
                {user.isActive ? 'Deactivate User' : 'Activate User'}
              </Button>
            </Card.Body>
          </Card>

          {/* Account Info */}
          <Card>
            <Card.Header>
              <h2 className="text-lg font-semibold text-secondary-900">
                Account Info
              </h2>
            </Card.Header>
            <Card.Body className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-secondary-600">Created</span>
                <span className="text-sm text-secondary-900">
                  {new Date(user.createdAt).toLocaleDateString()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-secondary-600">Updated</span>
                <span className="text-sm text-secondary-900">
                  {new Date(user.updatedAt).toLocaleDateString()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-secondary-600">Last Login</span>
                <span className="text-sm text-secondary-900">
                  {user.lastLoginAt
                    ? new Date(user.lastLoginAt).toLocaleDateString()
                    : 'Never'}
                </span>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Deactivate Modal */}
      <Modal
        isOpen={isDeactivateModalOpen}
        onClose={() => setIsDeactivateModalOpen(false)}
        title={user.isActive ? 'Deactivate User' : 'Activate User'}
        size="sm"
      >
        <Modal.Body>
          <p className="text-secondary-600">
            Are you sure you want to {user.isActive ? 'deactivate' : 'activate'}{' '}
            <span className="font-medium text-secondary-900">
              {user.firstName} {user.lastName}
            </span>
            ?
          </p>
          {user.isActive && (
            <p className="mt-2 text-sm text-secondary-500">
              The user will no longer be able to access the platform.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setIsDeactivateModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant={user.isActive ? 'danger' : 'primary'}
            onClick={handleToggleStatus}
            isLoading={isProcessing}
          >
            {user.isActive ? 'Deactivate' : 'Activate'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        isOpen={isResetPasswordModalOpen}
        onClose={() => setIsResetPasswordModalOpen(false)}
        title="Reset Password"
        size="sm"
      >
        <Modal.Body>
          <p className="text-secondary-600">
            Send a password reset email to{' '}
            <span className="font-medium text-secondary-900">{user.email}</span>?
          </p>
          <p className="mt-2 text-sm text-secondary-500">
            The user will receive an email with instructions to reset their password.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setIsResetPasswordModalOpen(false)}
          >
            Cancel
          </Button>
          <Button onClick={handleResetPassword} isLoading={isProcessing}>
            Send Reset Email
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Role Change Confirmation Modal */}
      <Modal
        isOpen={isRoleChangeModalOpen}
        onClose={() => {
          setIsRoleChangeModalOpen(false);
          setPendingRole(null);
        }}
        title="Change User Role"
        size="sm"
      >
        <Modal.Body>
          <p className="text-secondary-600">
            Are you sure you want to change{' '}
            <span className="font-medium text-secondary-900">
              {user.firstName} {user.lastName}
            </span>
            's role from{' '}
            <span className="font-medium text-secondary-900">{user.role}</span> to{' '}
            <span className="font-medium text-secondary-900">{pendingRole}</span>?
          </p>
          <div className="mt-4 p-3 bg-warning-50 rounded-lg">
            <p className="text-sm text-warning-700">
              This will change the user's permissions and access levels.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setIsRoleChangeModalOpen(false);
              setPendingRole(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={confirmRoleChange} isLoading={isProcessing}>
            Confirm Change
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserDetail;
